/**
 * Anvelopa de energie care animă orbul: un singur număr 0..1, citit de fiecare
 * cadru. Are două regimuri, iar separarea lor e tot rostul fișierului:
 *
 * - **real** - amplitudinea RMS a audio-ului chiar acum (microfonul în
 *   `listening`, redarea în `speaking`). Ăsta e scopul: orbul respiră pe vocea
 *   adevărată, nu pe o animație care se preface că ascultă.
 * - **sintetic** - o pulsație generată, pentru stările care n-au sunet deloc
 *   (`idle`, `processing`) și ca plasă de siguranță cât timp providerul de voce
 *   nu e încă conectat.
 *
 * Prototipul (`docs/prototypes/presence.html`) avea DOAR regimul sintetic, cu
 * bursts aleatoare inclusiv pe `speaking`/`listening`. Aici aleatoriul rămâne
 * doar unde nu există sunet; dacă un nivel real a sosit recent, el câștigă.
 *
 * `STALE_MS` e ce face trecerea înapoi automată: când audio-ul se oprește
 * (sesiune închisă, tab suspendat, provider căzut) nivelele pur și simplu nu mai
 * vin. Fără pragul ăsta anvelopa ar îngheța pe ultima valoare primită și orbul
 * ar rămâne blocat la jumătate de gură deschisă - simptomul clasic al unui
 * vizualizator legat direct la buffer.
 */

export type PresenceState = "idle" | "listening" | "processing" | "speaking";

/** Peste atât fără niciun nivel real, ne întoarcem la pulsația sintetică. */
const STALE_MS = 250;

/**
 * RMS-ul vorbirii normale pe PCM în [-1,1] stă cam între 0.05 și 0.25 - deci
 * fără amplificare orbul abia s-ar mișca. Factorul urcă vorbirea obișnuită spre
 * partea de sus a intervalului, iar `Math.min` taie strigătul: peste plafon
 * forma nu mai crește, ca un vizitator care țipă în microfon să nu deformeze
 * widgetul.
 */
const REAL_GAIN = 3.2;

/**
 * Netezirea: cât de repede urmărește valoarea curentă ținta. ~70ms e pragul sub
 * care mișcarea începe să pară nervoasă pe silabe, iar peste ~120ms vocea și
 * forma se desincronizează vizibil.
 */
const SMOOTH_MS = 70;

/**
 * Un cadru nu are voie să sară oricât: la revenirea dintr-un tab ascuns `dt`
 * poate fi de ordinul minutelor, iar netezirea ar deveni un salt instantaneu.
 */
const MAX_DT_MS = 100;

export class EnergyEnvelope {
  private value = 0;
  private target = 0;
  private lastBurst = 0;
  private realLevel = 0;
  private realAt = Number.NEGATIVE_INFINITY;

  /** Perioada respirației în repaus, în secunde (tokenul `pulse` din prototip). */
  constructor(private readonly restPeriodS = 6) {}

  /**
   * Nivel RMS (0..1) măsurat pe bufferul audio curent. Chemat de stratul de
   * captare sau de redare, nu de bucla de desen.
   */
  feed(level: number, now: number): void {
    this.realLevel = Math.max(0, Math.min(1, level));
    this.realAt = now;
  }

  /** Avansează un cadru și întoarce energia 0..1. */
  step(state: PresenceState, now: number, dtMs: number): number {
    const dt = Math.max(0, Math.min(MAX_DT_MS, dtMs));
    const fresh = now - this.realAt < STALE_MS;
    const audible = state === "listening" || state === "speaking";

    if (audible && fresh) {
      this.target = Math.min(1, this.realLevel * REAL_GAIN);
    } else if (audible) {
      // Stare cu sunet, dar fără nivele: providerul încă se conectează sau a
      // căzut. Bursts ca în prototip - `listening` mai domol decât `speaking`,
      // fiindcă ascultarea nu trebuie să pară că vorbește ea.
      const ceiling = state === "speaking" ? 1 : 0.55;
      if (now - this.lastBurst > 90 + Math.random() * 140) {
        this.lastBurst = now;
        this.target = (Math.random() < 0.18 ? 0.06 : 0.25 + Math.random() * 0.7) * ceiling;
      }
    } else if (state === "processing") {
      // Gândirea are un ritm propriu, mai strâns decât respirația - se citește ca
      // efort, nu ca repaus.
      this.target = 0.1 + 0.05 * Math.sin(now / 300);
    } else {
      this.target = 0.05 + 0.028 * Math.sin(((now / 1000) * Math.PI * 2) / this.restPeriodS);
    }

    this.value += (this.target - this.value) * Math.min(1, dt / SMOOTH_MS);
    return this.value;
  }

  get level(): number {
    return this.value;
  }
}

/**
 * RMS peste un bloc PCM float. Trăiește aici, lângă singurul lui consumator, ca
 * să fie testabil fără AudioContext (jsdom n-are Web Audio).
 */
export function rms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
  return Math.sqrt(sum / samples.length);
}
